// Queue access — who may open /queue: a business with its own queue subscription, OR an active employee
// (sub-account) of a business, who works that business's queue ("Operating as"). The server enforces which
// business queues an operator may use (requireQueueOperator); this only decides whether to offer the page.

import { useEffect, useState } from 'react';
import { useWallet } from '../contexts/WalletContext.tsx';
import { useSubscription } from '../contexts/SubscriptionContext.tsx';
import { listBusinesses } from '../api/subAccounts.ts';

export type QueueAccess = Readonly<{
  readonly known: boolean;     // enough is loaded to decide
  readonly allowed: boolean;
  readonly businesses: ReadonlyArray<string>;   // active businesses this user works for (handles)
}>;

// Sub-account membership changed in this tab (approved, joined, left, removed): the nav's Queue tab
// re-checks. Without it a just-approved waiter had no Queue tab until they reloaded.
const SUBACCOUNTS_CHANGED = 'chainsale:subaccounts-changed';
export const notifySubAccountsChanged = (): void => { window.dispatchEvent(new Event(SUBACCOUNTS_CHANGED)); };

export const canOperateQueue = (hasOwnQueue: boolean, activeBusinessCount: number): boolean =>
  hasOwnQueue || activeBusinessCount > 0;

export const useQueueAccess = (): QueueAccess => {
  const { connectedWallet } = useWallet();
  const { hasFeature, state: subState } = useSubscription();
  const sessionId = connectedWallet?.sessionId ?? null;
  const [businesses, setBusinesses] = useState<ReadonlyArray<string> | null>(null);

  useEffect(() => {
    setBusinesses(null);
    if (sessionId === null) return;
    let cancelled = false;
    const load = (): void => {
      void listBusinesses(sessionId).then((result) => {
        if (cancelled) return;
        setBusinesses(result.ok
          ? Object.freeze(result.value.filter((b) => b.status === 'active').map((b) => b.parent_handle))
          : Object.freeze([]));
      });
    };
    // Also re-check when the app comes back to the foreground (approved/removed while it was away)
    const onVisible = (): void => { if (document.visibilityState === 'visible') load(); };
    load();
    window.addEventListener(SUBACCOUNTS_CHANGED, load);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      window.removeEventListener(SUBACCOUNTS_CHANGED, load);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [sessionId]);

  const ownQueue = hasFeature('queue');
  const subscriptionKnown = !subState.loading && (subState.status !== null || subState.error !== null);
  const list = businesses ?? [];
  return Object.freeze({
    known: ownQueue || (subscriptionKnown && businesses !== null),
    allowed: canOperateQueue(ownQueue, list.length),
    businesses: list,
  });
};
